import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import {
  Search,
  RefreshCw,
  AlertCircle,
  FileX,
  ArrowLeft,
  Trash2,
  FileMinus,
  X,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../Apis/apiConfig";
import "../Style/Member.css";
import "../Style/FinancialTables.css";
import "../Style/Dashboard.css";
import Swal from "sweetalert2";

const DeleteDebitNote = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debitNotes, setDebitNotes] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = debitNotes.length;
  const totalPages = Math.ceil(totalItems / perPage);

  // Filter debit notes based on search term
  const filteredDebitNotes = useMemo(() => {
    return debitNotes.filter(
      (note) =>
        note.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.unitNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.billNo?.toString().includes(searchTerm) ||
        note.debitAmount?.toString().includes(searchTerm),
    );
  }, [debitNotes, searchTerm]);

  const paginated = filteredDebitNotes.slice(
    (page - 1) * perPage,
    page * perPage,
  );
  const displayedTotal = filteredDebitNotes.length;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handlePageChange = (p) => p >= 1 && p <= totalPages && setPage(p);
  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleRefresh = () => {
    fetchDebitNotes();
  };

  const handleDeleteAll = async () => {
    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: "This will delete ALL deleted debit notes permanently! This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const societyName = localStorage.getItem("society");

          await axios.delete(
            `${BASE_URL}/debit-delete/delete-all-deleted-debit-notes`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: { societyName },
            },
          );

          setDebitNotes([]);
          return true;
        } catch (err) {
          console.error("Error deleting all debit notes:", err);
          Swal.showValidationMessage("Failed to delete all debit notes");
          return false;
        }
      },
    });

    if (result.isConfirmed && result.value) {
      Swal.fire(
        "Deleted!",
        "All deleted debit notes have been removed.",
        "success",
      );
    }
  };

  const fetchDebitNotes = async () => {
    const societyName = localStorage.getItem("society");
    const token = localStorage.getItem("token");

    if (!societyName || !token) {
      setError("Missing society name or authentication token");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${BASE_URL}/debit-delete/get-deleted-debit-note`,
        null,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("DEBIT NOTES API Response:", response.data);

      if (response.data && Array.isArray(response.data)) {
        setDebitNotes(response.data);
      } else {
        setDebitNotes([]);
        setError("No data returned from server");
      }
    } catch (err) {
      console.error("Error fetching deleted debit notes:", err);
      setError(
        err.response?.data?.message || "Failed to fetch deleted debit notes",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebitNotes();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${
              totalPages === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return "-";
    const num = parseFloat(amount);
    return isNaN(num) ? amount : `₹${num.toLocaleString("en-IN")}`;
  };

  return (
    <div className="app-container">
      <style>
        {`
        /* ========== RECEIPT PAGE OPTIMIZATIONS ========== */
        .app-container {
          height: 100vh;
          overflow: hidden !important;
        }

        .main-layout {
          height: calc(100vh - 64px);
          overflow: hidden !important;
        }

        .content-area {
          height: 100%;
          overflow-y: auto !important;
          overflow-x: hidden;
          padding-bottom: 20px;
        }

        .financial-container,
        .financial-card {
          height: auto !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .financial-body {
          height: auto !important;
          overflow: visible !important;
          padding: 0 !important;
        }

        /* ========== COMPACT TABLE CONTROLS ========== */
        .controls-left,
        .controls-center,
        .controls-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .controls-center {
          flex: 1;
          justify-content: center;
        }

        .entries-selector label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }

        .entries-select {
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
          font-size: 12px;
          color: #334155;
          cursor: pointer;
          height: 28px;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 4px;
          border: none;
        }

        .pagination-nav {
          padding: 4px 10px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #334155;
          font-size: 11px;
          font-weight: 500;
          height: 26px;
          transition: all 0.2s;
        }

        .pagination-nav:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-nav:not(:disabled):hover {
          background: #f1f5f9;
        }

        .pagination-btn {
          min-width: 26px;
          height: 26px;
          padding: 4px 8px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #334155;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-btn:hover {
          background: #f1f5f9;
        }

        .pagination-btn.active {
          background: #0d6efd;
          color: white;
          border-color: #0d6efd;
        }

        .pagination-ellipsis {
          padding: 0 4px;
          color: #94a3b8;
          font-size: 11px;
        }

        .entries-info {
          font-size: 11px;
          color: #64748b;
          white-space: nowrap;
        }

        /* ========== TABLE OPTIMIZATION ========== */
        .financial-table-container {
          overflow-x: auto;
          padding: 0;
          margin: 0;
        }

        .financial-table,
        .data-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        .financial-table th,
        .data-table th {
          padding: 6px 8px !important;
          text-align: center !important;
          vertical-align: middle;
          white-space: nowrap;
          font-weight: 600;
          font-size: 11px !important;
          background-color: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          line-height: 1.2 !important;
          height: 32px !important;
        }

        .financial-table td,
        .data-table td {
          padding: 5px 8px !important;
          text-align: center !important;
          vertical-align: middle;
          font-size: 10px !important;
          border-bottom: 1px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          line-height: 1.3 !important;
          height: 36px !important;
        }

        /* SR NO Column */
        .financial-table th:nth-child(1),
        .financial-table td:nth-child(1),
        .data-table th:first-child,
        .data-table td:first-child {
          width: 35px !important;
          min-width: 35px !important;
          max-width: 35px !important;
          padding: 5px 2px !important;
        }

        /* Actions Column */
        .financial-table th:last-child,
        .financial-table td:last-child,
        .data-table th:last-child,
        .data-table td:last-child {
          width: 80px !important;
          min-width: 80px !important;
          padding: 4px 2px !important;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 4px;
          padding: 0;
          margin: 0;
        }

        .action-icon-btn,
        .icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 3px;
          border-radius: 3px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          width: 24px;
          height: 24px;
        }

        .action-icon-btn svg,
        .icon-btn svg {
          width: 14px;
          height: 14px;
        }

        .action-icon-btn:hover,
        .icon-btn:hover {
          background-color: #f1f5f9;
        }

        .icon-btn.edit {
          color: #0d6efd;
        }

        /* Checkbox */
        .financial-table input[type="checkbox"],
        .data-table input[type="checkbox"] {
          margin: 0;
          width: 13px;
          height: 13px;
          vertical-align: middle;
          cursor: pointer;
        }

        /* Row Hover */
        .financial-table tbody tr:hover td,
        .data-table tbody tr:hover td {
          background-color: #f8fafc;
        }

        .selected-row td {
          background-color: #e3f2fd !important;
        }

        /* Cell Content */
        .cell-content {
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        /* Status Badges */
        .status-badge {
          padding: 2px 6px !important;
          border-radius: 10px !important;
          font-size: 9px !important;
          font-weight: 600;
          display: inline-block;
          line-height: 1.2;
        }

        .status-paid {
          background-color: #d4edda;
          color: #155724;
        }

        .status-pending {
          background-color: #fff3cd;
          color: #856404;
        }

        .status-overdue {
          background-color: #f8d7da;
          color: #721c24;
        }

        /* Empty State */
        .financial-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          min-height: 300px;
        }

        .financial-state h3 {
          margin: 16px 0 8px 0;
          color: #334155;
          font-size: 18px;
          font-weight: 600;
        }

        .financial-state p {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        /* Loading Animation */
        .spin-animation {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .controls-left,
          .controls-center,
          .controls-right {
            width: 100%;
            justify-content: space-between;
          }
        }
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              <div className="flex px-4 bg-blue-400 rounded-tr-lg rounded-tl-lg text-white py-2">
                <div className="controls-left">
                  <button
                    onClick={() => navigate(-1)}
                    className="back-button"
                    title="Go back"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        "rgba(255,255,255,0.2)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <ArrowLeft size={16} />
                  </button>

                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>

                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input type="text" placeholder="Search deleted debit notes..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setPage(1);
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={handleRefresh}
                      disabled={loading}
                      title="Refresh deleted debit notes list"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={handleDeleteAll}
                      title="Delete all debit note history records"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="controls-right">
                  {!loading && displayedTotal > 0 && (
                    <span className="entries-info text-white">
                      {(page - 1) * perPage + 1}-
                      {Math.min(page * perPage, displayedTotal)} of{" "}
                      {displayedTotal}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      Prev
                    </button>

                    {renderPageNumbers()}

                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Deleted Debit Notes</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={fetchDebitNotes}
                      style={{
                        marginTop: "20px",
                        padding: "10px 20px",
                        background: "#0d6efd",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : debitNotes.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Deleted Debit Notes Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm
                          ? "No deleted debit notes match your search criteria."
                          : "There are no deleted debit notes recorded in the history."}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>SR NO.</th>
                          <th>MEMBER NAME</th>
                          <th>UNIT NO</th>
                          <th>BILL NO</th>
                          <th>DEBIT AMOUNT</th>
                          <th>DEBIT DATE</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((row, idx) => {
                          const absoluteIdx = (page - 1) * perPage + idx;
                          const debitDate = row.debitDate
                            ? new Date(row.debitDate)
                            : null;
                          const timeAgo = debitDate
                            ? Math.floor(
                                (Date.now() - debitDate) /
                                  (1000 * 60 * 60 * 24),
                              ) > 0
                              ? `${Math.floor((Date.now() - debitDate) / (1000 * 60 * 60 * 24))} days ago`
                              : "Today"
                            : "";

                          return (
                            <tr key={row.debitNoteDeletedId || idx}>
                              <td>{absoluteIdx + 1}</td>
                              <td>{row.memberName || "-"}</td>
                              <td>{row.unitNo || "-"}</td>
                              <td>{row.billNo || "-"}</td>
                              <td
                                style={{ color: "#dc3545", fontWeight: "600" }}
                              >
                                {formatAmount(row.debitAmount)}
                              </td>
                              <td>{formatDate(row.debitDate)}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="icon-btn edit"
                                    title="View Debit Note Details"
                                    onClick={() => {
                                      Swal.fire({
                                        title: `<div style="text-align: center;">
                                                  <div style="width: 50px; height: 50px; margin: 0 auto 10px; background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(238, 90, 82, 0.2);">
                                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                                                      <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                  </div>
                                                  <h3 style="margin: 0; color: #2d3436; font-size: 18px; font-weight: 600;">Deleted Debit Note</h3>
                                                </div>`,
                                        html: `
                                          <div style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;">
                                            <!-- Debit Note Info -->
                                            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding: 12px; background: #f8f9fa; border-radius: 10px;">
                                              <div style="width: 44px; height: 44px; background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 600; font-size: 16px;">
                                                D
                                              </div>
                                              <div style="flex: 1;">
                                                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                                                  <h4 style="margin: 0; color: #2d3436; font-size: 16px; font-weight: 600;">${row.memberName || "Unknown Member"}</h4>
                                                  <span style="background: #ffeaa7; color: #d35400; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600;">
                                                    ${timeAgo}
                                                  </span>
                                                </div>
                                                <p style="margin: 0; color: #636e72; font-size: 13px; text-align: left;">
                                                  Unit ${row.unitNo || "N/A"} • Bill #${row.billNo || "N/A"}
                                                </p>
                                              </div>
                                            </div>

                                            <!-- Details Grid -->
                                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px;">
                                              <!-- Debit Amount -->
                                              <div style="background: #ffe6e6; padding: 10px; border-radius: 8px; border-left: 3px solid #dc3545;">
                                                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2">
                                                    <path d="M12 1v22M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" />
                                                  </svg>
                                                  <span style="font-size: 12px; color: #dc3545; font-weight: 600;">DEBIT AMOUNT</span>
                                                </div>
                                                <p style="margin: 0; color: #2d3436; font-size: 16px; font-weight: 600; text-align: center;">
                                                  ${formatAmount(row.debitAmount)}
                                                </p>
                                              </div>

                                              <!-- Debit Date -->
                                              <div style="background: #f0f7ff; padding: 10px; border-radius: 8px; border-left: 3px solid #9b59b6;">
                                                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9b59b6" stroke-width="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                  </svg>
                                                  <span style="font-size: 12px; color: #9b59b6; font-weight: 600;">DEBIT DATE</span>
                                                </div>
                                                <p style="margin: 0; color: #2d3436; font-size: 13px; font-weight: 500; text-align: center;">
                                                  ${formatDate(row.debitDate)}
                                                </p>
                                              </div>
                                            </div>

                                            <!-- Additional Info -->
                                            <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 15px;">
                                              <div style="display: flex; align-items: flex-start; gap: 10px;">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#27ae60" stroke-width="2" style="margin-top: 2px; flex-shrink: 0;">
                                                  <path d="M12 16v-4m0-4h.01" />
                                                  <circle cx="12" cy="12" r="10" />
                                                </svg>
                                                <div style="flex: 1;">
                                                  <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
                                                    <span style="font-size: 12px; color: #27ae60; font-weight: 600;">ADDITIONAL INFO</span>
                                                  </div>
                                                  <p style="margin: 0; color: #2d3436; font-size: 13px; text-align: left;">
                                                    ${row.narration || row.remark || row.description || "No additional information available"}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>

                                            <!-- Footer Note -->
                                            <div style="text-align: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                                              <p style="margin: 0; color: #7f8c8d; font-size: 11px; display: flex; align-items: center; justify-content: center; gap: 6px;">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7f8c8d" stroke-width="2">
                                                  <circle cx="12" cy="12" r="10" />
                                                  <line x1="12" y1="8" x2="12" y2="12" />
                                                  <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                                This debit note record is preserved for reference only
                                              </p>
                                            </div>
                                          </div>
                                        `,
                                        width: 480,
                                        padding: "20px",
                                        showConfirmButton: true,
                                        confirmButtonText: "Close",
                                        confirmButtonColor: "#667eea",
                                        showCloseButton: true,
                                        customClass: {
                                          popup: "swal2-popup-compact",
                                          title: "swal2-title-compact",
                                        },
                                      });
                                    }}
                                  >
                                    <Eye size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DeleteDebitNote;
